import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ArrowRightLeft, CheckCircle, AlertTriangle, TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';
import { Transaction } from '@/types/transaction';
import { useAccountsStore, useFormatters } from '@flows.cash/core';
import { format } from 'date-fns';
import { PotentialTransferMatch, getMatchCriteriaKeys } from './utils/transferDetection';
import { StyledModal, StyledModalButton } from '@/components/ui/styled-modal';

interface TransferMergeModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceTransaction: Transaction | null;
  potentialMatches: PotentialTransferMatch[];
  onConfirmMerge: (sourceTransaction: Transaction, matchingTransaction: Transaction) => void;
  onCreateTransferOnly: () => void;
}

export const TransferMergeModal: React.FC<TransferMergeModalProps> = ({
  isOpen,
  onClose,
  sourceTransaction,
  potentialMatches,
  onConfirmMerge,
  onCreateTransferOnly
}) => {
  const { t } = useTranslation();
  const { accounts } = useAccountsStore();
  const { formatCurrencyPreferences } = useFormatters();
  
  // Confirmation state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<PotentialTransferMatch | null>(null);

  const getAccountName = (accountId: string) => {
    return accounts?.find(acc => acc.id === accountId)?.name || 'Unknown Account';
  };

  // Handle match selection with confirmation
  const handleMatchClick = (match: PotentialTransferMatch) => {
    setSelectedMatch(match);
    setShowConfirmation(true);
  };

  // Handle confirmed merge
  const handleConfirmedMerge = () => {
    if (sourceTransaction && selectedMatch) {
      onConfirmMerge(sourceTransaction, selectedMatch.matchingTransaction);
      setShowConfirmation(false);
      setSelectedMatch(null);
    }
  };

  // Handle cancel confirmation
  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setSelectedMatch(null);
  };

  const getTransactionIcon = (type?: string) => {
    switch (type) {
      case 'income':
        return <TrendingUp className="w-4 h-4 text-positive dark:text-positive-dark" />;
      case 'expense':
        return <TrendingDown className="w-4 h-4 text-negative dark:text-negative-dark" />;
      default:
        return <ArrowRightLeft className="w-4 h-4 text-secondary dark:text-primary-dark" />;
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return 'text-positive bg-positive/10 dark:text-positive-dark dark:bg-positive-dark/10';
    if (confidence >= 75) return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-400/10';
    return 'text-text-secondary bg-background-input dark:text-text-dark-secondary dark:bg-background-dark-input';
  };

  const renderTransactionCard = (transaction: Transaction, title: string) => (
    <div className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 rounded-xl p-4 border border-border dark:border-border-dark">
      <div className="flex items-center gap-2 mb-3">
        {getTransactionIcon(transaction.type)}
        <h4 className="font-medium text-text-primary dark:text-text-dark-primary">{title}</h4>
      </div>
      <div className="text-sm text-text-secondary dark:text-text-dark-secondary mb-2">
        {getAccountName(transaction.account_id)}
      </div>
      <div className="space-y-2">
        <div className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
          {formatCurrencyPreferences(transaction.amount, transaction.currency)}
        </div>
        <div className="text-sm text-text-secondary dark:text-text-dark-secondary">
          {format(new Date(transaction.date), 'PPp')}
        </div>
        {transaction.description && (
          <div className="text-sm text-text-primary dark:text-text-dark-primary line-clamp-2">
            {transaction.description}
          </div>
        )}
      </div>
    </div>
  );

  const renderMatchCard = (match: PotentialTransferMatch, index: number) => (
    <div
      key={match.matchingTransaction.id}
      className="border border-border dark:border-border-dark bg-background-secondary/50 dark:bg-background-dark-secondary/50 backdrop-blur-lg backdrop-saturate-150 rounded-xl p-4 hover:bg-secondary/10 hover:border-secondary dark:hover:bg-primary-dark/10 dark:hover:border-primary-dark cursor-pointer transition-all duration-200"
      onClick={() => handleMatchClick(match)}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-positive dark:text-positive-dark" />
          <span className="font-medium text-text-primary dark:text-text-dark-primary">
            {t('transactions.transferMerge.potentialMatch')} #{index + 1}
          </span>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-medium ${getConfidenceColor(match.confidence)}`}>
          {Math.round(match.confidence)}%
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-3">
          {getTransactionIcon(match.matchingTransaction.type)}
          <div className="flex-1">
            <div className="font-medium text-text-primary dark:text-text-dark-primary">
              {getAccountName(match.matchingTransaction.account_id)}
            </div>
            <div className="text-sm text-text-secondary dark:text-text-dark-secondary">
              {formatCurrencyPreferences(match.matchingTransaction.amount, match.matchingTransaction.currency)}
            </div>
          </div>
        </div>
        
        <div className="text-sm text-text-secondary dark:text-text-dark-secondary">
          {format(new Date(match.matchingTransaction.date), 'PPp')}
        </div>
        
        {match.matchingTransaction.description && (
          <div className="text-sm text-text-primary dark:text-text-dark-primary line-clamp-2">
            {match.matchingTransaction.description}
          </div>
        )}

        <div>
          <div className="text-xs text-text-secondary dark:text-text-dark-secondary mb-2">
            {t('transactions.transferMerge.matchCriteria')}:
          </div>
          <div className="flex flex-wrap gap-1">
            {getMatchCriteriaKeys(match).map((criteriaKey, idx) => (
              <span key={idx} className="px-2 py-1 bg-secondary/10 text-secondary dark:bg-primary-dark/10 dark:text-primary-dark text-xs rounded-lg">
                {t(criteriaKey)}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <>
      <StyledModal
        isOpen={isOpen && !showConfirmation}
        onClose={onClose}
        title={t('transactions.transferMerge.title')}
        maxWidth="1000px"
        footer={
          <>
            <StyledModalButton
              onClick={onClose}
              variant="outline"
            >
              {t('common.cancel')}
            </StyledModalButton>
            <StyledModalButton
              onClick={onCreateTransferOnly}
              variant="primary"
            >
              {t('transactions.transferMerge.createTransferOnly')}
            </StyledModalButton>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-[#2F2F2F] dark:text-[#A3A3A3]">
            {t('transactions.transferMerge.subtitle')}
          </p>
            {sourceTransaction && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-text-primary dark:text-text-dark-primary mb-3">
                  {t('transactions.transferMerge.sourceTransaction')}
                </h3>
                {renderTransactionCard(sourceTransaction, t('transactions.transferMerge.originalTransaction'))}
              </div>
            )}

            {potentialMatches.length > 0 ? (
              <div>
                <h3 className="text-lg font-medium text-text-primary dark:text-text-dark-primary mb-3">
                  {t('transactions.transferMerge.potentialMatches')} ({potentialMatches.length})
                </h3>
                <div className="space-y-3">
                  {potentialMatches.map((match, index) => renderMatchCard(match, index))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                <p className="text-text-secondary dark:text-text-dark-secondary">
                  {t('transactions.transferMerge.noMatches')}
                </p>
              </div>
            )}
        </div>
      </StyledModal>

      {/* Confirmation Dialog */}
      <StyledModal
        isOpen={showConfirmation && !!selectedMatch && !!sourceTransaction}
        onClose={handleCancelConfirmation}
        title={t('transactions.transferMerge.confirmMerge')}
        maxWidth="500px"
        footer={
          <>
            <StyledModalButton
              onClick={handleCancelConfirmation}
              variant="outline"
            >
              {t('common.cancel')}
            </StyledModalButton>
            <StyledModalButton
              onClick={handleConfirmedMerge}
              variant="primary"
            >
              {t('transactions.transferMerge.confirmMergeButton')}
            </StyledModalButton>
          </>
        }
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="w-5 h-5 text-yellow-500" />
            <p className="text-sm text-[#2F2F2F] dark:text-[#A3A3A3]">
              {t('transactions.transferMerge.confirmMergeDescription')}
            </p>
          </div>

          {selectedMatch && sourceTransaction && (
            <div className="space-y-4">
              {/* Source Transaction Summary */}
              <div className="bg-[#F3F4F6] dark:bg-[#1E2B42]/50 rounded-lg p-3">
                <div className="text-xs text-[#2F2F2F] dark:text-[#A3A3A3] mb-1">
                  {t('transactions.transferMerge.sourceTransaction')}
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-[#1C1C1C] dark:text-[#E8E8E8]">
                    {getAccountName(sourceTransaction.account_id)}
                  </div>
                  <div className="text-sm font-semibold text-[#1C1C1C] dark:text-[#E8E8E8]">
                    {formatCurrencyPreferences(sourceTransaction.amount, sourceTransaction.currency)}
                  </div>
                </div>
              </div>

              {/* Arrow */}
              <div className="flex justify-center">
                <ArrowRightLeft className="w-4 h-4 text-[#2F2F2F] dark:text-[#A3A3A3]" />
              </div>

              {/* Target Transaction Summary */}
              <div className="bg-[#F3F4F6] dark:bg-[#1E2B42]/50 rounded-lg p-3">
                <div className="text-xs text-[#2F2F2F] dark:text-[#A3A3A3] mb-1">
                  {t('transactions.transferMerge.targetTransaction')}
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-[#1C1C1C] dark:text-[#E8E8E8]">
                    {getAccountName(selectedMatch.matchingTransaction.account_id)}
                  </div>
                  <div className="text-sm font-semibold text-[#1C1C1C] dark:text-[#E8E8E8]">
                    {formatCurrencyPreferences(selectedMatch.matchingTransaction.amount, selectedMatch.matchingTransaction.currency)}
                  </div>
                </div>
                <div className="mt-2">
                  <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getConfidenceColor(selectedMatch.confidence)}`}>
                    {Math.round(selectedMatch.confidence)}% {t('transactions.transferMerge.confidence')}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </StyledModal>
    </>
  );
}; 